/**
 * Cookie Consent Manager - DSGVO-konform
 * Lokal, ohne externe Services
 */

// ============================================================================
// IIFE Wrapper - verhindert "duplicate variable" Fehler
// ============================================================================

(function() {
    'use strict';

interface CookieConsentState {
  necessary: boolean;
  analytics: boolean;
  timestamp: string | null;
  version: string;
}

interface CookieConsentAPI {
  hasConsent(category: string): boolean;
  showSettings(): void;
  getState(): CookieConsentState;
}

// Storage key
const STORAGE_KEY = 'cyberplex-cookie-consent';

// Cookie consent state
let consentState: CookieConsentState = {
  necessary: true,  // Always true
  analytics: false,
  timestamp: null,
  version: '1.0'
};

/**
 * Initialize cookie consent system
 */
function init(): void {
  loadConsent();
  
  // Check if we need to show the banner
  if (!hasValidConsent()) {
    // Delay slightly to let page load first
    setTimeout(function() {
      showBanner();
    }, 500);
  }
  
  // Setup event listeners
  setupEventListeners();
  
  // Make consent checker available globally
  window.CookieConsent = {
    hasConsent: hasConsent,
    showSettings: showBanner,
    getState: function() { return consentState; }
  };
}

/**
 * Load consent from localStorage
 */
function loadConsent(): void {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as Partial<CookieConsentState>;
      // Validate structure
      if (parsed && typeof parsed.necessary === 'boolean') {
        consentState = { ...consentState, ...parsed };
      }
    }
  } catch (e) {
    console.warn('Cookie consent: Could not load saved preferences');
  }
}

/**
 * Save consent to localStorage
 */
function saveConsent(): void {
  try {
    consentState.timestamp = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consentState));
  } catch (e) {
    console.warn('Cookie consent: Could not save preferences');
  }
}

/**
 * Check if valid consent exists
 */
function hasValidConsent(): boolean {
  return consentState.timestamp !== null;
}

/**
 * Check if specific cookie category is consented
 */
function hasConsent(category: string): boolean {
  if (category === 'necessary') {
    return true; // Necessary cookies always allowed
  }
  return consentState[category as keyof CookieConsentState] === true;
}

/**
 * Show the cookie banner
 */
function showBanner(): void {
  const banner = document.getElementById('cookieBanner');
  if (banner) {
    banner.classList.add('cp-cookie-banner--active');
    banner.setAttribute('aria-hidden', 'false');
    
    // Focus management for accessibility
    const firstFocusable = banner.querySelector('button, input') as HTMLElement | null;
    if (firstFocusable) {
      firstFocusable.focus();
    }
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  }
}

/**
 * Hide the cookie banner
 */
function hideBanner(): void {
  const banner = document.getElementById('cookieBanner');
  if (banner) {
    banner.classList.remove('cp-cookie-banner--active');
    banner.setAttribute('aria-hidden', 'true');
    
    // Restore body scroll
    document.body.style.overflow = '';
  }
}

/**
 * Setup all event listeners
 */
function setupEventListeners(): void {
  // Decline all optional cookies
  const declineBtn = document.getElementById('cookieDecline');
  if (declineBtn) {
    declineBtn.addEventListener('click', function() {
      consentState.analytics = false;
      saveConsent();
      hideBanner();
      applyConsent();
    });
  }
  
  // Save selected preferences
  const saveBtn = document.getElementById('cookieSave');
  if (saveBtn) {
    saveBtn.addEventListener('click', function() {
      const analyticsToggle = document.getElementById('analyticsCookies') as HTMLInputElement | null;
      if (analyticsToggle) {
        consentState.analytics = analyticsToggle.checked;
      }
      saveConsent();
      hideBanner();
      applyConsent();
    });
  }
  
  // Accept all cookies
  const acceptAllBtn = document.getElementById('cookieAcceptAll');
  if (acceptAllBtn) {
    acceptAllBtn.addEventListener('click', function() {
      consentState.analytics = true;
      // Also check the toggle visually
      const analyticsToggle = document.getElementById('analyticsCookies') as HTMLInputElement | null;
      if (analyticsToggle) {
        analyticsToggle.checked = true;
      }
      saveConsent();
      hideBanner();
      applyConsent();
    });
  }
  
  // Footer settings link
  const settingsLink = document.getElementById('cookieSettingsLink');
  if (settingsLink) {
    settingsLink.addEventListener('click', function(e: Event) {
      e.preventDefault();
      // Sync toggle state with current consent
      const analyticsToggle = document.getElementById('analyticsCookies') as HTMLInputElement | null;
      if (analyticsToggle) {
        analyticsToggle.checked = consentState.analytics;
      }
      showBanner();
    });
  }
  
  // Close on backdrop click
  const banner = document.getElementById('cookieBanner');
  if (banner) {
    banner.addEventListener('click', function(e: Event) {
      if (e.target === banner) {
        // Optional: close on backdrop click
        // hideBanner();
      }
    });
  }
  
  // Keyboard support (Escape to close if already consented)
  document.addEventListener('keydown', function(e: KeyboardEvent) {
    if (e.key === 'Escape' && hasValidConsent()) {
      hideBanner();
    }
  });
}

/**
 * Apply consent settings to website functionality
 */
function applyConsent(): void {
  // Trigger custom event for other scripts
  const event = new CustomEvent('cookieConsentChanged', {
    detail: consentState
  });
  document.dispatchEvent(event);
  
  // Apply analytics if consented
  if (consentState.analytics) {
    enableAnalytics();
  }
}

/**
 * Enable local analytics (anonymous, no external services)
 */
function enableAnalytics(): void {
  // Example: Local page view tracking
  // This is completely anonymous and stores no personal data
  try {
    let pageViews = localStorage.getItem('cyberplex-pageviews') || '0';
    pageViews = (parseInt(pageViews, 10) + 1).toString();
    localStorage.setItem('cyberplex-pageviews', pageViews);
  } catch (e) {
    // Silent fail
  }
}

/**
 * Check if analytics should run
 */
function shouldRunAnalytics(): boolean {
  return hasConsent('analytics');
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Expose shouldRunAnalytics globally
window.shouldRunAnalytics = shouldRunAnalytics;

})();
